import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { states } from "capitalrx-types";
import { Checkbox, Dropdown, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";
import { validate } from "capitalrx-components-2.0";

import { formatZipCode } from "@/utils";
import { ProviderManagement } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { ProviderPaymentRemittanceDataForm } from "./PaymentRemittanceGroupFields";

export interface FieldsRemittanceTypeProps {
  readonly readOnly?: boolean;
  readonly provider: ProviderManagement.ProviderSchema;
}

export const FieldsRemittanceType: FC<FieldsRemittanceTypeProps> = ({ readOnly, provider }) => {
  const { threeFull, sixFull, twelve } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { control, setValue, getValues, watch } = useFormContext<ProviderPaymentRemittanceDataForm>();
  const [remittanceType, remitCheck, paymentType] = useWatch({
    control,
    name: ["remittance_type", "remit.check", "payment_type"],
  });

  useEffect(() => {
    if (!paymentType) return; // Keeps the Remittance Type Radio Options unchecked until Payment Type is selected
    else if (paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK)
      setValue("remittance_type", ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER);
    else setValue("remittance_type", ProviderManagement.REMITTANCE_TYPE_VALUES.ELECTRONIC);
  }, [paymentType]);

  useEffect(() => {
    const { unsubscribe } = watch((value, { type, name }) => {
      if (type === "change" && name === "remit.check") {
        if (value.remit.check) {
          setValue("remit.street_address", provider.mailing_address_line_1);
          setValue("remit.address_line", provider.mailing_address_line_2);
          setValue("remit.city", provider.mailing_address_city);
          setValue("remit.state", provider.mailing_address_state);
          setValue("remit.zip_code", provider.mailing_address_zip_code);
        } else {
          setValue("remit.street_address", "");
          setValue("remit.address_line", "");
          setValue("remit.city", "");
          setValue("remit.state", "");
          setValue("remit.zip_code", "");
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [setValue, getValues, watch, provider]);

  // Watch remit.zip_code for formatting
  const [remitZip] = useWatch({
    control,
    name: ["remit.zip_code"],
  });

  // Format zip code: insert dash for ZIP+4
  useEffect(() => {
    if (remitZip !== undefined) {
      const f = formatZipCode(remitZip);
      if (f !== remitZip) {
        setValue("remit.zip_code", f, { shouldValidate: false });
      }
    }
  }, [remitZip, setValue]);

  return (
    <>
      <Stack>
        {/* This next portion is commented as per ad-hoc design detailed in https://capitalrx.atlassian.net/browse/CAP-80855 since we'll need to bring it back for VCC */}
        {/* <Subheading>Remittance Type</Subheading>
        <RadioGroup.Field
          name="remittance_type"
          hideLabel
          orientation="horizontal"
          readOnly
        >
          <RadioGroup.Option value={ProviderManagement.REMITTANCE_TYPE_VALUES.ELECTRONIC}>Electronic</RadioGroup.Option>
          <RadioGroup.Option value={ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER}>Paper</RadioGroup.Option>
        </RadioGroup.Field>
        <Message appearance={Messages.Informational} visible={remittanceType === ProviderManagement.REMITTANCE_TYPE_VALUES.ELECTRONIC}>835s will be available for download in the Provider Portal</Message>
        <Message appearance={Messages.Informational} visible={remittanceType === ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER && paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK}>EOPs will be sent to the Provider’s mailing address</Message> */}
      </Stack>
      {remittanceType === ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER && paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && (
        <Stack>
          <Subheading>Remit To Address</Subheading>
          <Grid>
            <Grid.Spread columns={twelve}>
              <Checkbox.Field
                label="Same as Mailing Address"
                name="remit.check"
                testId="remit_check"
                hideLabel
                readOnly={readOnly || !provider?.mailing_address_line_1}
              />
            </Grid.Spread>
          </Grid>
          {!remitCheck
            && <Grid>
              <Grid.Spread columns={twelve}>
                <Textbox.Field
                  label="Street Address"
                  name="remit.street_address"
                  testId="remit_street_address"
                  required
                  readOnly={readOnly || remitCheck}
                />
                <Textbox.Field
                  label="Address Line 2"
                  name="remit.address_line"
                  testId="remit_address_line"
                  readOnly={readOnly || remitCheck}
                />
              </Grid.Spread>
              <Grid.Break />
              <Grid.Span columns={sixFull}>
                <Textbox.Field label="City" name="remit.city" testId="remit_city" required readOnly={readOnly || remitCheck} />
              </Grid.Span>
              <Grid.Spread columns={threeFull}>
                <Dropdown.Field label="State" name="remit.state" testId="remit_state" readOnly={readOnly || remitCheck} searchable required>
                  {Object.entries(states).map(([full, abbreviation]) => {
                    return (
                      <Dropdown.Option key={abbreviation} value={abbreviation}>
                        {full}
                      </Dropdown.Option>
                    );
                  })}
                </Dropdown.Field>
                <Textbox.Field
                  label="Zip Code"
                  name="remit.zip_code"
                  testId="remit_zip_code"
                  readOnly={readOnly || remitCheck}
                  validator={(value) => validate.isZipCode(value)}
                  required
                />
              </Grid.Spread>
            </Grid>
          }
        </Stack>
      )}
    </>
  );
};
